import React, {useEffect, useRef, useState} from 'react';
import {Link, useHistory} from 'react-router-dom';
import apiClient from "../../../services/api-client";
import {Toast} from 'primereact/toast';
import {Dialog} from 'primereact/dialog';
import AppConfirmDialog from '../../../components/ui/AppConfirmDialog';
import GuestPaymentForm from "./components/GuestPaymentForm";
import {ProgressSpinner} from "primereact/progressspinner";
import logo from '../../../assets/images/logo.png';
import AnimatedSuccessGIF from '../../../assets/images/check-green.gif';
import AnimatedWarningGIF from '../../../assets/images/warning.gif';

// .replace(...) strips any trailing slash(es) from REACT_APP_DOVESAPI so concatenating with
// threeDsInitUrl (which already starts with "/") below never produces "http://host//path" --
// a double slash there fails Spring Security's exact-path matcher for /visa-3ds-init.html and
// falls through to .anyRequest().authenticated(), which is what was producing the 403.
const DOVES_API_BASE = (() => {
  let base = process.env.REACT_APP_DOVESAPI || '';
  while (base.endsWith('/')) base = base.slice(0, -1);
  return base;
})();

// The 3-D Secure iframe is served by the Doves API, so only messages from its origin are trusted.
const DOVES_API_ORIGIN = (() => {
  try {
    return new URL(DOVES_API_BASE, window.location.origin).origin;
  } catch (e) {
    return window.location.origin;
  }
})();

const GuestPayment = () => {
  const toast = useRef(null);
  const history = useHistory();

  const [paymentMethod, setPaymentMethod] = useState('');
  const [policyNumber, setPolicyNumber] = useState('');
  const [payerName, setPayerName] = useState('');
  const [emailAddress, setEmailAddress] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [amount, setAmount] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [expiryMonth, setExpiryMonth] = useState('');
  const [expiryYear, setExpiryYear] = useState('');
  const [cvv, setCvv] = useState('');
  const [cardHolderName, setCardHolderName] = useState('');

  const [isPaymentLoading, setIsPaymentLoading] = useState(false);
  const [activeView, setActiveView] = useState('FORM');
  const [serverResponse, setServerResponse] = useState({});
  const [secondsLeft, setSecondsLeft] = useState(30);

  // 3-D Secure: the token identifies the in-flight session on the backend, and initUrl points at
  // the backend-served static page that actually talks to iVeri (rendered inside the Dialog
  // below). The guest checkout page has no other dialog today, so this is its own.
  const [isThreeDsDialogVisible, setIsThreeDsDialogVisible] = useState(false);
  const [threeDsInitUrl, setThreeDsInitUrl] = useState(null);

  const validateEcocash = () => {
    const emailValid = /^[^\s@]+@[^\s@.]+\.[^\s@]+$/.test(emailAddress);
    return (
      policyNumber.trim() !== '' &&
      payerName.trim() !== '' &&
      emailValid &&
      phoneNumber.trim() !== '' &&
      Number(amount) > 0
    );
  };


  const submitPaymentRequest = (e) => {
    e.preventDefault();

    if (paymentMethod === 'Pay with Card') {
      if (policyNumber.trim() === '') {
        toast.current.show({ severity: 'warn', summary: 'Policy Required', detail: 'Please enter a policy number to proceed.', life: 5000 });
        return;
      }
      // Start the new Visa flow
      startVisaPaymentFlow();
    } else {
      if (!validateEcocash()) {
        toast.current.show({ severity: 'warn', summary: 'Make Payment', detail: 'Please fill in your policy number, full name, email, phone number, and amount.', life: 9000 });
        return;
      }
      confirm();
    }
  };

  const submitEcocashPaymentToServer = async () => {
    try {
      const payload = {
        operation: 'make-payment',
        requestBody: {
          policyNumber,
          productName: 'payment for policy number ' + policyNumber,
          productID: policyNumber,
          notes: 'guest payment for policy number ' + policyNumber,
          paymentMethod: 'ecocash',
          mobileNumber: phoneNumber,
          payerName,
          billingAddress: payerName,
          emailAddress,
          amount: Number(amount),
        },
      };

      const response = await apiClient.post('payment/guest/ecocash', payload);

      if (response.data.status === 'SUCCESS') {
        setIsPaymentLoading(true);
        startCountDown(30, response.data);
        setServerResponse(response.data);
        setActiveView('SUBMITTED_REQUEST');
      } else {
        setIsPaymentLoading(false);
        setServerResponse(response.data);
        setActiveView('RECEIVED_FAILURE');
      }
    } catch (error) {
      setIsPaymentLoading(false);
      setServerResponse({ message: error.response?.data?.message || error.message });
      setActiveView('RECEIVED_FAILURE');
    }
  };

  // Listens for the postMessage the backend sends once
  // iVeri's 3-D Secure ceremony (running inside the Dialog's iframe) has finished and the payment
  // has been completed server-side.
  useEffect(() => {
    const handleThreeDsMessage = (event) => {
      if (event.origin !== DOVES_API_ORIGIN) return;
      const data = event.data;
      if (data?.type !== 'VISA_PAYMENT_SUCCESS') return;

      setIsThreeDsDialogVisible(false);
      setIsPaymentLoading(false);
      
      setServerResponse({ responseBody: { paymentStatus: 'PAID', paymentReference: data.reference } });
      setActiveView('RECEIVED_SUCCESS');
    };

    window.addEventListener('message', handleThreeDsMessage);
    return () => window.removeEventListener('message', handleThreeDsMessage);
  }, []);

  const startVisaPaymentFlow = async () => {
    setIsPaymentLoading(true);
    try {
      // First verify the policy and get the usual premium
      const response = await apiClient.get('user/verify-policy', {
        params: { identifier: policyNumber.trim() }
      });
      
      if (response.data.status !== 'SUCCESS') {
        throw new Error(response.data.message || 'Policy not found');
      }
      
      const v = response.data.responseBody;
      const usualPremium = v.usualPremium || 0;
      
      // Open the iframe modal with the correct URL
      const iframeUrl = `${DOVES_API_BASE}/visa-payment.html?policyNumber=${v.policyNumber}&premium=${usualPremium}`;
      setThreeDsInitUrl(iframeUrl);
      setIsThreeDsDialogVisible(true);
      setIsPaymentLoading(false);
      // Open the secure payment page automatically as soon as the user confirms --
      // no longer an optional fallback the user has to remember to click.
      window.open(iframeUrl, '_blank');
      
    } catch (error) {
      setIsPaymentLoading(false);
      toast.current.show({ severity: 'error', summary: 'Verification Failed', detail: error.response?.data?.message || error.message || 'Could not verify policy', life: 9000 });
    }
  };

  const verifyPayment = async (responseData) => {
    try {
      const reference = responseData.responseBody.paymentReference;
      const response = await apiClient.get('payment/guest/verifyPayment/' + reference);

      if (response.data.status === 'SUCCESS') {
        switch (response.data.responseBody.paymentStatus) {
          case 'AWAITING DELIVERY':
          case 'PAID':
          case 'DELIVERED': {
            setIsPaymentLoading(false);
            setServerResponse(response.data);
            setActiveView('RECEIVED_SUCCESS');
            break;
          }
          default: {
            setIsPaymentLoading(false);
            setServerResponse(response.data);
            setActiveView('VERIFY_REQUEST');
            break;
          }
        }
      } else {
        setIsPaymentLoading(false);
        setActiveView('VERIFY_REQUEST');
      }
    } catch (error) {
      setServerResponse({ message: error.response?.data?.message || error.message });
      setActiveView('RECEIVED_FAILURE');
    }
  };

  const startCountDown = (seconds, responseData) => {
    const interval = setInterval(() => {
      if (seconds > 0) {
        seconds--;
        setSecondsLeft(seconds);
      } else {
        verifyPayment(responseData);
        clearInterval(interval);
      }
    }, 1000);
  };

  const confirm = () => {
    const modal = document.getElementById('guest_payment_confirm_modal');
    if (modal) modal.showModal();
  };

  const resetForm = () => {
    setIsPaymentLoading(false);
    setActiveView('FORM');
    setServerResponse({});
  };

  const methodLabel = paymentMethod === 'Pay with Card' ? 'Visa Card' : paymentMethod;

  return (
    <>
      <Toast ref={toast} />
      <AppConfirmDialog
        id="guest_payment_confirm_modal"
        title="Confirm your payment details?"
        message={`Are you sure you want to securely submit this payment of ${amount ? '$' + amount : ''} towards policy ${policyNumber}?`}
        onConfirm={() => {
          setIsPaymentLoading(true);
          if (paymentMethod === 'Pay with Card') {
            startVisaPaymentFlow();
          } else {
            submitEcocashPaymentToServer();
          }
        }}
        confirmStyle="btn-primary text-white border-none"
        cancelStyle="btn-outline"
      />

      <Dialog
        header="Secure Card Verification"
        visible={isThreeDsDialogVisible}
        style={{ width: '95vw', maxWidth: '600px' }}
        onHide={() => setIsThreeDsDialogVisible(false)}
      >
        <div className="mb-4 text-center">
            <p className="text-sm text-slate-600 mb-2">We've opened a secure payment tab for you to complete verification. You can also finish it below.</p>
            <button
                type="button"
                className="btn btn-outline btn-primary w-full"
                onClick={() => {
                    window.open(threeDsInitUrl, '_blank');
                    // We don't close the dialog here, we let the postMessage close it
                    // so the user still sees the loading state on the dashboard
                }}
            >
                <i className="pi pi-external-link mr-2"></i> Reopen in New Tab
            </button>
            <p className="text-xs text-slate-500 mt-2">If the new tab didn't open (your browser may have blocked it) or the page below fails to load, use the button above.</p>
        </div>
        <iframe
          title="3D Secure Verification"
          src={threeDsInitUrl || 'about:blank'}
          style={{ width: '100%', height: '600px', border: 'none' }}
        />
      </Dialog>

      
      <div className="min-h-screen bg-slate-50 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8">
        {activeView === 'FORM' ? (
          <div className="max-w-5xl w-full mx-auto">
            <div className="text-center mb-10">
              <Link to="/landing" className="inline-block">
                <img className="h-12 w-auto mx-auto mb-6" src={logo} alt="Doves" />
              </Link>
              <div className="flex items-center justify-center gap-2 mb-2">
                <span className="badge badge-primary bg-primary/10 text-primary border-none font-bold uppercase tracking-wider text-xs px-3 py-3 rounded-full">Guest Checkout</span>
              </div>
              <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Make a Payment</h1>
              <p className="mt-3 text-slate-500 max-w-xl mx-auto">Pay towards your policy without signing in. All payments are securely processed in USD.</p>
            </div>

            <div className="bg-white shadow-xl shadow-slate-200/50 rounded-3xl overflow-hidden border border-slate-100 flex flex-col lg:flex-row">
              {/* Form Section */}
              <div className="flex-1 p-6 sm:p-10 lg:p-12">
                <GuestPaymentForm
                  paymentMethod={paymentMethod}
                  policyNumber={policyNumber}
                  payerName={payerName}
                  emailAddress={emailAddress}
                  phoneNumber={phoneNumber}
                  amount={amount}
                  cardNumber={cardNumber}
                  expiryMonth={expiryMonth}
                  expiryYear={expiryYear}
                  cvv={cvv}
                  cardHolderName={cardHolderName}
                  handlePaymentMethodChange={setPaymentMethod}
                  handlePolicyNumberChange={(e) => setPolicyNumber(e.target.value)}
                  handlePayerNameChange={(e) => setPayerName(e.target.value)}
                  handleEmailAddressChange={(e) => setEmailAddress(e.target.value)}
                  handlePhoneNumberChange={(e) => setPhoneNumber(e.target.value)}
                  handleAmountChange={(e) => setAmount(e.target.value)}
                  handleCardNumberChange={(e) => setCardNumber(e.target.value.replace(/[^\d]/g, ''))}
                  handleExpiryMonthChange={(e) => setExpiryMonth(e.target.value.replace(/[^\d]/g, ''))}
                  handleExpiryYearChange={(e) => setExpiryYear(e.target.value.replace(/[^\d]/g, ''))}
                  handleCvvChange={(e) => setCvv(e.target.value.replace(/[^\d]/g, ''))}
                  handleCardHolderNameChange={(e) => setCardHolderName(e.target.value)}
                  submitPaymentRequest={submitPaymentRequest}
                  isSubmitting={isPaymentLoading}
                />
              </div>

              {/* Summary Section */}
              <div className="w-full lg:w-[380px] bg-slate-50 p-6 sm:p-10 lg:p-12 border-t lg:border-t-0 lg:border-l border-slate-100 flex flex-col">
                <h3 className="text-sm font-bold text-slate-700 mb-6 uppercase tracking-wider">Payment Summary</h3>
                
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 mb-6">
                  <p className="text-slate-500 text-sm font-semibold mb-1">Amount due</p>
                  <p className="text-4xl font-extrabold text-[#135A9C] tracking-tight flex items-baseline gap-2">
                    {amount ? `${amount}` : '$0.00'}
                    <span className="text-sm font-bold text-slate-400">USD</span>
                  </p>
                </div>

                <div className="space-y-4 mb-8 flex-1">
                  <div className="flex justify-between items-center py-3 border-b border-slate-200/60">
                    <span className="text-slate-500 text-sm">Policy number</span>
                    <span className="font-semibold text-slate-900">{policyNumber || '—'}</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-slate-200/60">
                    <span className="text-slate-500 text-sm">Payment method</span>
                    <span className="font-semibold text-slate-900">{methodLabel || '—'}</span>
                  </div>
                </div>

                <div className="flex items-start gap-3 text-slate-400 text-xs mt-auto bg-white p-4 rounded-xl border border-slate-100">
                  <i className="pi pi-lock text-lg text-slate-300"></i>
                  <p>Payments are processed securely via Ecocash, Paynow and iVeri. We never store your card details.</p>
                </div>
              </div>
            </div>

            <p className="text-center mt-8 text-slate-500">
              Have an account? <Link className="text-[#135A9C] font-bold hover:underline" to="/signin">Sign In</Link>
            </p>
          </div>
        ) : (
          <div className="max-w-md w-full mx-auto">
            <div className="text-center mb-8">
              <Link to="/landing" className="inline-block">
                <img className="h-10 w-auto mx-auto" src={logo} alt="Doves" />
              </Link>
            </div>
            
            <div className="bg-white shadow-xl shadow-slate-200/50 rounded-3xl p-8 border border-slate-100 text-center">
              {activeView === 'SUBMITTED_REQUEST' && (
                <div className="flex flex-col items-center">
                  <div className="mb-6">
                    <ProgressSpinner style={{ width: '64px', height: '64px' }} strokeWidth="4" fill="var(--surface-ground)" animationDuration=".5s" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">Check your phone</h3>
                  <p className="text-slate-500 mb-6">Kindly authorise your Ecocash payment on your phone. This will verify automatically in {secondsLeft}s.</p>
                  {serverResponse?.responseBody && (
                    <div className="bg-slate-50 px-4 py-2 rounded-lg border border-slate-100">
                      <span className="text-slate-400 text-sm mr-2">Reference:</span>
                      <strong className="text-slate-700 tracking-wider font-mono">{serverResponse.responseBody.paymentReference}</strong>
                    </div>
                  )}
                </div>
              )}

              {activeView === 'VERIFY_REQUEST' && (
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center text-3xl mb-6 shadow-inner">
                    <i className="pi pi-hourglass"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">Still confirming</h3>
                  <p className="text-slate-500 mb-6">We haven't received confirmation yet.</p>
                  {serverResponse?.responseBody && (
                    <div className="bg-slate-50 px-4 py-2 rounded-lg border border-slate-100 mb-8">
                      <span className="text-slate-400 text-sm mr-2">Reference:</span>
                      <strong className="text-slate-700 tracking-wider font-mono">{serverResponse.responseBody.paymentReference}</strong>
                    </div>
                  )}
                  <div className="flex flex-col sm:flex-row gap-3 w-full justify-center">
                    <button type="button" className="btn btn-outline border-slate-200 text-slate-600 hover:bg-slate-50 rounded-xl" onClick={resetForm}>
                      <i className="pi pi-times"></i> Cancel
                    </button>
                    <button type="button" className="btn btn-primary rounded-xl" onClick={() => verifyPayment(serverResponse)}>
                      <i className="pi pi-refresh"></i> Retry Verification
                    </button>
                  </div>
                </div>
              )}

              {activeView === 'RECEIVED_FAILURE' && (
                <div className="flex flex-col items-center">
                  <img className="w-20 h-20 mb-6" src={AnimatedWarningGIF} alt="Failed" />
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">Payment failed</h3>
                  <p className="text-slate-500 mb-8">{serverResponse?.message || 'Your payment could not be processed.'}</p>
                  <button type="button" className="btn btn-primary w-full rounded-xl" onClick={resetForm}>
                    <i className="pi pi-refresh"></i> Try Again
                  </button>
                </div>
              )}

              {activeView === 'RECEIVED_SUCCESS' && (
                <div className="flex flex-col items-center">
                  <img className="w-24 h-24 mb-4" src={AnimatedSuccessGIF} alt="Success" />
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">Payment successful!</h3>
                  <p className="text-slate-500 mb-6">Thank you for your payment towards policy <strong className="text-slate-700">{policyNumber}</strong>.</p>
                  {serverResponse?.responseBody?.paymentReference && (
                    <div className="bg-emerald-50 text-emerald-800 px-4 py-3 rounded-xl border border-emerald-100 w-full mb-8">
                      <span className="text-sm mr-2 opacity-80">Reference:</span>
                      <strong className="tracking-wider font-mono">{serverResponse.responseBody.paymentReference}</strong>
                    </div>
                  )}
                  <button type="button" className="btn btn-primary w-full rounded-xl" onClick={() => history.push('/landing')}>
                    <i className="pi pi-check-circle"></i> Return to Home
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default GuestPayment;
